import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useRef, useState, useCallback, useEffect } from 'react';
import { isSameMonth, isWithinInterval, startOfMonth } from 'date-fns';
import Icon from '../Icon/Icon.js';
import MonthBlock from './MonthBlock.js';
import { getFirstSelectedMonthIndex, useNrOfMonthsDisplayedDesktop, isSmallScreen, isMonthSelected } from './utils.js';
import { DatePickerWrapper, DatePickerInnerWrapper, DatePickerHeader, DatePickerNav, DatePickerBody, DatePickerMonths, MonthHeaderMobileSticky } from './style.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useEffectOnce from '../../hooks/useEffectOnce.js';
import { ICON_UI_CHEVRON_LEFT } from '../../icons/ICON_UI_CHEVRON_LEFT.js';
import { ICON_UI_CHEVRON_RIGHT } from '../../icons/ICON_UI_CHEVRON_RIGHT.js';

var _excluded = ["disableStickyMonthHeader", "monthFooter", "nrOfMonths", "offers", "onChange", "onDynamicRangeChange", "recommendedDates", "selectedDate", "selectedDateInterval", "selectedMonths", "startDate", "useDynamicRangeSelect", "validDates"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var selectedDateIntervalDefault = {};

/**
 * DatePicker component for selecting single dates or date ranges, with support for multiple months, offers, and recommended dates.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} [props.disableStickyMonthHeader=false] - If true, disables the sticky month header on mobile.
 * @param {function} [props.monthFooter=() => {}] - Function to render a footer for each month.
 * @param {number} [props.nrOfMonths=3] - Number of months to display in the picker.
 * @param {Array} [props.offers=[]] - Array of offer objects, each containing a `month` and `offers` array.
 * @param {function} [props.onChange=() => {}] - Callback fired when a date is selected.
 * @param {function} [props.onDynamicRangeChange=() => {}] - Callback fired when the dynamic date range changes.
 * @param {Array} [props.recommendedDates=[]] - Array of recommended date strings.
 * @param {string} [props.selectedDate=''] - Currently selected date string.
 * @param {Object} [props.selectedDateInterval={}] - Selected date interval with `from` and `to` properties.
 * @param {Array} [props.selectedMonths=[]] - Array of selected month objects.
 * @param {string|Date} props.startDate - The start date for the calendar view.
 * @param {boolean} [props.useDynamicRangeSelect=false] - If true, enables dynamic range selection mode.
 * @param {Array} [props.validDates=[]] - Array of valid date strings that can be selected.
 * @returns {import('react').JSX.Element} The rendered DatePicker component.
 */
var DatePicker = function DatePicker(_ref) {
  var _ref$disableStickyMon = _ref.disableStickyMonthHeader,
    disableStickyMonthHeader = _ref$disableStickyMon === void 0 ? false : _ref$disableStickyMon,
    _ref$monthFooter = _ref.monthFooter,
    monthFooter = _ref$monthFooter === void 0 ? function () {} : _ref$monthFooter,
    _ref$nrOfMonths = _ref.nrOfMonths,
    nrOfMonths = _ref$nrOfMonths === void 0 ? 3 : _ref$nrOfMonths,
    _ref$offers = _ref.offers,
    offers = _ref$offers === void 0 ? [] : _ref$offers,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {} : _ref$onChange,
    _ref$onDynamicRangeCh = _ref.onDynamicRangeChange,
    onDynamicRangeChange = _ref$onDynamicRangeCh === void 0 ? function () {} : _ref$onDynamicRangeCh,
    _ref$recommendedDates = _ref.recommendedDates,
    recommendedDates = _ref$recommendedDates === void 0 ? [] : _ref$recommendedDates,
    _ref$selectedDate = _ref.selectedDate,
    selectedDate = _ref$selectedDate === void 0 ? '' : _ref$selectedDate,
    _ref$selectedDateInte = _ref.selectedDateInterval,
    selectedDateInterval = _ref$selectedDateInte === void 0 ? selectedDateIntervalDefault : _ref$selectedDateInte,
    _ref$selectedMonths = _ref.selectedMonths,
    selectedMonths = _ref$selectedMonths === void 0 ? [] : _ref$selectedMonths,
    startDate = _ref.startDate,
    _ref$useDynamicRangeS = _ref.useDynamicRangeSelect,
    useDynamicRangeSelect = _ref$useDynamicRangeS === void 0 ? false : _ref$useDynamicRangeS,
    _ref$validDates = _ref.validDates,
    validDates = _ref$validDates === void 0 ? [] : _ref$validDates,
    rest = _objectWithoutProperties(_ref, _excluded);
  var listRef = useRef(null);
  var datePickerWrapperRef = useRef(null);
  var datePickerBodyRef = useRef(null);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    datePickerBodyElement = _useState2[0],
    setDatePickerBody = _useState2[1];
  var _useState3 = useState(selectedDateInterval),
    _useState4 = _slicedToArray(_useState3, 2),
    internalDateInterval = _useState4[0],
    setInternalDateInterval = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    dateHovered = _useState6[0],
    setDateHovered = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    dynamicRangeInProgress = _useState8[0],
    setDynamicRangeInProgress = _useState8[1];
  var _useState9 = useState(getFirstSelectedMonthIndex(selectedMonths, selectedDate, selectedDateInterval, startDate)),
    _useState10 = _slicedToArray(_useState9, 2),
    monthInLeftPos = _useState10[0],
    setMonthInLeftPos = _useState10[1];
  var nrOfMonthsDisplayedDesktop = useNrOfMonthsDisplayedDesktop(datePickerWrapperRef);
  var navigatePrev = useCallback(function () {
    setMonthInLeftPos(monthInLeftPos - 1);
  }, [setMonthInLeftPos, monthInLeftPos]);
  var navigateNext = useCallback(function () {
    setMonthInLeftPos(monthInLeftPos + 1);
  }, [setMonthInLeftPos, monthInLeftPos]);
  useEffectOnce(function () {
    if (!datePickerBodyElement) setDatePickerBody(datePickerBodyRef.current);
  });
  useEffect(function () {
    setInternalDateInterval(selectedDateInterval);
  }, [selectedDateInterval]);
  useEffect(function () {
    if (isSmallScreen()) {
      setTimeout(function () {
        return listRef.current.children[monthInLeftPos].scrollIntoView();
      }, 100);
    }
  }, [monthInLeftPos, listRef]);
  var handleOnChange = useCallback(function (e, dateStr) {
    if (!useDynamicRangeSelect) {
      onChange(e, dateStr);
    } else {
      var from = internalDateInterval.from,
        to = internalDateInterval.to;
      if (!!from && !!to) {
        setInternalDateInterval({
          from: dateStr,
          to: null
        });
        setDynamicRangeInProgress(true);
        setDateHovered(dateStr);
        onDynamicRangeChange(e, {
          from: dateStr,
          to: null,
          inProgress: true
        });
      } else {
        setDynamicRangeInProgress(false);
        setInternalDateInterval({
          from: from,
          to: dateStr
        });
        setDateHovered(null);
        onDynamicRangeChange(e, {
          from: from,
          to: dateStr,
          inProgress: false
        });
      }
    }
  }, [internalDateInterval, onChange, onDynamicRangeChange, useDynamicRangeSelect]);
  var renderMonths = function renderMonths() {
    var months = [];
    var selectedDateObj = new Date(selectedDate);
    var startDateObj = new Date(startDate);
    var startMonth = startDateObj.getMonth();
    var startYear = startDateObj.getFullYear();
    var _ref2 = (datePickerBodyElement === null || datePickerBodyElement === void 0 ? void 0 : datePickerBodyElement.getBoundingClientRect()) || {},
      left = _ref2.left,
      right = _ref2.right;
    var _loop = function _loop() {
      var _offers$find;
      var monthIndex = startMonth + index;
      var currentMonth = monthIndex % 12;
      var addYear = Math.floor(monthIndex / 12);
      var firstOfMonth = new Date(startYear + addYear, currentMonth, 1);
      var year = firstOfMonth.getFullYear();
      var offersInMonth = ((_offers$find = offers.find(function (monthOffer) {
        return isSameMonth(firstOfMonth, new Date(monthOffer.month));
      })) === null || _offers$find === void 0 ? void 0 : _offers$find.offers) || [];
      var monthIsSelected = (selectedMonths === null || selectedMonths === void 0 ? void 0 : selectedMonths.length) > 0 && isMonthSelected(firstOfMonth, selectedMonths);

      /*
        isWithinSelected and isInHoveredRange is used
        to prevent re-renders for months not affected
      */
      var isWithinSelected = selectedDateObj.getFullYear() === year && selectedDateObj.getMonth() === currentMonth;
      var isInHoveredRange = dynamicRangeInProgress && dateHovered ? isWithinInterval(firstOfMonth, {
        start: startOfMonth(new Date(internalDateInterval.from)),
        end: new Date(dateHovered)
      }) : false;
      var datePickerHasOffers = (offers === null || offers === void 0 ? void 0 : offers.length) > 0;
      var showRecommendedDates = !datePickerHasOffers && !useDynamicRangeSelect;
      var isVisible = index >= monthInLeftPos && index < monthInLeftPos + nrOfMonthsDisplayedDesktop;
      months.push(/*#__PURE__*/jsx(MonthBlock, {
        selectedDate: isWithinSelected && !useDynamicRangeSelect ? selectedDate : null,
        selectedDateInterval: internalDateInterval,
        onChange: handleOnChange,
        monthFooter: monthFooter,
        month: currentMonth,
        year: year,
        validDates: validDates,
        recommendedDates: showRecommendedDates ? recommendedDates : [],
        useDynamicRangeSelect: useDynamicRangeSelect,
        dateHovered: isInHoveredRange ? dateHovered : null,
        setDateHovered: setDateHovered,
        dynamicRangeInProgress: dynamicRangeInProgress,
        offers: (offersInMonth === null || offersInMonth === void 0 ? void 0 : offersInMonth.length) > 0 ? offersInMonth : null,
        datePickerHasOffers: datePickerHasOffers,
        datePickerHasSelectedMonths: (selectedMonths === null || selectedMonths === void 0 ? void 0 : selectedMonths.length) > 0,
        monthIsSelected: monthIsSelected,
        nrOfMonthsDisplayedDesktop: nrOfMonthsDisplayedDesktop,
        navigateToNextMonth: navigateNext,
        navigateToPrevMonth: navigatePrev,
        isMonthBlockVisible: isVisible,
        datePickerBodyPosition: {
          left: left,
          right: right
        }
      }, "".concat(year, "-").concat(currentMonth)));
    };
    for (var index = 0; index < nrOfMonths; index += 1) {
      _loop();
    }
    return months;
  };
  var leftScroll = nrOfMonthsDisplayedDesktop === 3 ? monthInLeftPos * 33.33 : monthInLeftPos * 50;
  return /*#__PURE__*/jsx(DatePickerWrapper, _objectSpread(_objectSpread({
    ref: datePickerWrapperRef
  }, rest), {}, {
    children: /*#__PURE__*/jsxs(DatePickerInnerWrapper, {
      children: [/*#__PURE__*/jsxs(DatePickerHeader, {
        children: [/*#__PURE__*/jsx(DatePickerNav, {
          hidden: monthInLeftPos <= 0,
          onClick: navigatePrev,
          className: "sg-datepicker__nav-prev",
          type: "button",
          children: /*#__PURE__*/jsx(Icon, {
            path: ICON_UI_CHEVRON_LEFT,
            size: "xs"
          })
        }), /*#__PURE__*/jsx(DatePickerNav, {
          hidden: monthInLeftPos >= nrOfMonths - 2,
          onClick: navigateNext,
          className: "sg-datepicker__nav-next",
          type: "button",
          children: /*#__PURE__*/jsx(Icon, {
            path: ICON_UI_CHEVRON_RIGHT,
            size: "xs"
          })
        })]
      }), /*#__PURE__*/jsx(DatePickerBody, {
        ref: datePickerBodyRef,
        children: /*#__PURE__*/jsxs(DatePickerMonths, {
          ref: listRef,
          style: {
            transform: "translateX(-".concat(isSmallScreen() ? 0 : leftScroll, "%)")
          },
          children: [!disableStickyMonthHeader && /*#__PURE__*/jsx(MonthHeaderMobileSticky, {}), renderMonths()]
        })
      })]
    })
  }));
};
var DatePicker$1 = DatePicker;

export { DatePicker$1 as default };
//# sourceMappingURL=DatePicker.js.map
